/**
 * GlobalView — Aircraft Detail Popup
 *
 * Overlay showing all available details for the selected aircraft.
 * Appears when an aircraft is selected, dismisses on close or ESC.
 * Draggable via the header bar.
 */

import { useEffect, useRef } from 'react';
import { useGlobeStore } from '../../stores/globeStore';
import { useEntityStore } from '../../stores/entityStore';
import { stopTracking } from '../../services/aircraftTracker';
import { getCategoryLabel } from '../../services/layers/aircraftIcons';
import { useDraggable } from '../../hooks/useDraggable';
import { useIsMobile } from '../../hooks/useIsMobile';
import EntityBottomSheet from './EntityBottomSheet';
import styles from './AircraftPopup.module.css';

// ─── Formatters ──────────────────────────────────────────────────

function fmtCoord(val: number, decimals = 5): string {
  return val.toFixed(decimals);
}

function fmtAlt(meters: number): string {
  if (meters >= 10_000) return `${(meters / 1000).toFixed(1)} km`;
  return `${Math.round(meters)} m`;
}

function fmtAltFt(meters: number): string {
  return `${Math.round(meters * 3.28084).toLocaleString()} ft`;
}

function fmtSpeed(ms: number): string {
  const kt = ms * 1.944;
  const kmh = ms * 3.6;
  return `${Math.round(kt)} kt (${Math.round(kmh)} km/h)`;
}

function fmtVerticalRate(ms: number): string {
  const ftMin = ms * 196.85;
  return `${ftMin >= 0 ? '+' : ''}${Math.round(ftMin)} ft/min`;
}

function fmtHeading(deg: number): string {
  return `${Math.round(deg)}°`;
}

function fmtFlightLevel(altMeters: number): string {
  return `FL${Math.round(altMeters / 30.48).toString().padStart(3, '0')}`;
}

function timeAgo(isoStr: string): string {
  const diff = (Date.now() - new Date(isoStr).getTime()) / 1000;
  if (diff < 5) return 'jetzt';
  if (diff < 60) return `vor ${Math.round(diff)}s`;
  return `vor ${Math.round(diff / 60)}min`;
}

/** Map country ISO code to flag emoji (origin_country from OpenSky) */
function countryFlag(origin: string): string {
  const COUNTRY_FLAGS: Record<string, string> = {
    'Switzerland': '🇨🇭',
    'Germany': '🇩🇪',
    'France': '🇫🇷',
    'Italy': '🇮🇹',
    'Austria': '🇦🇹',
    'United Kingdom': '🇬🇧',
    'United States': '🇺🇸',
    'Netherlands': '🇳🇱',
    'Belgium': '🇧🇪',
    'Spain': '🇪🇸',
    'Portugal': '🇵🇹',
    'Turkey': '🇹🇷',
    'Poland': '🇵🇱',
    'Czech Republic': '🇨🇿',
    'Czechia': '🇨🇿',
    'Sweden': '🇸🇪',
    'Norway': '🇳🇴',
    'Denmark': '🇩🇰',
    'Finland': '🇫🇮',
    'Ireland': '🇮🇪',
    'Luxembourg': '🇱🇺',
    'Canada': '🇨🇦',
    'Japan': '🇯🇵',
    'China': '🇨🇳',
    'Russia': '🇷🇺',
    'India': '🇮🇳',
    'Brazil': '🇧🇷',
    'Australia': '🇦🇺',
    'South Korea': '🇰🇷',
    'United Arab Emirates': '🇦🇪',
    'Saudi Arabia': '🇸🇦',
    'Qatar': '🇶🇦',
    'Singapore': '🇸🇬',
    'Thailand': '🇹🇭',
    'Greece': '🇬🇷',
    'Romania': '🇷🇴',
    'Hungary': '🇭🇺',
    'Croatia': '🇭🇷',
    'Iceland': '🇮🇸',
    'Israel': '🇮🇱',
    'Mexico': '🇲🇽',
    'Argentina': '🇦🇷',
  };
  return COUNTRY_FLAGS[origin] ?? '';
}

// ─── Component ───────────────────────────────────────────────────

// ─── Shared Content ──────────────────────────────────────────────

interface AircraftContentProps {
  entity: {
    position: { lat: number; lon: number; alt: number };
    heading: number | undefined;
    source: string;
    timestamp: string;
    properties: Record<string, unknown>;
  };
  trackedEntityId: string | null;
}

function AircraftContent({ entity, trackedEntityId }: AircraftContentProps) {
  const props = entity.properties;
  const category = props.category as number | undefined;
  const callsign = (props.callsign as string) || '—';
  const velocity = props.velocity as number | undefined;
  const verticalRate = props.verticalRate as number | undefined;
  const squawk = (props.squawk as string) || undefined;
  const onGround = props.onGround as boolean;
  const origin = (props.origin as string) || undefined;
  const baroAlt = props.baroAltitude as number | undefined;
  const heading = entity.heading;
  const airlineCode = callsign !== '—' && callsign.length >= 3 ? callsign.slice(0, 3) : undefined;

  return (
    <>
      {/* Tracking indicator */}
      {trackedEntityId && (
        <div className={styles.trackingBadge}>
          <span className={styles.trackingDot} />
          TRACKING
        </div>
      )}

      <div className={styles.section}>
        <div className={styles.sectionTitle}>FLUGINFO</div>
        <div className={styles.grid}>
          {category != null && <Row label="TYP" value={getCategoryLabel(category)} />}
          {airlineCode && <Row label="ICAO" value={airlineCode} />}
          {origin && <Row label="LAND" value={`${countryFlag(origin)} ${origin}`} />}
          {squawk && <Row label="SQK" value={squawk} />}
          <Row label="GND" value={onGround ? 'Ja' : 'Nein'} />
          <Row label="SRC" value={entity.source.toUpperCase()} />
        </div>
      </div>

      <div className={styles.section}>
        <div className={styles.sectionTitle}>POSITION</div>
        <div className={styles.grid}>
          <Row label="LAT" value={fmtCoord(entity.position.lat)} suffix={entity.position.lat >= 0 ? '° N' : '° S'} />
          <Row label="LON" value={fmtCoord(entity.position.lon)} suffix={entity.position.lon >= 0 ? '° E' : '° W'} />
          <Row label="ALT" value={fmtAlt(entity.position.alt)} />
          {entity.position.alt > 0 && <Row label="" value={fmtAltFt(entity.position.alt)} />}
          {baroAlt != null && <Row label="FL" value={fmtFlightLevel(baroAlt)} />}
        </div>
      </div>

      <div className={styles.section}>
        <div className={styles.sectionTitle}>BEWEGUNG</div>
        <div className={styles.grid}>
          {velocity != null && <Row label="SPD" value={fmtSpeed(velocity)} />}
          {heading != null && <Row label="HDG" value={fmtHeading(heading)} />}
          {verticalRate != null && verticalRate !== 0 && <Row label="VS" value={fmtVerticalRate(verticalRate)} />}
        </div>
      </div>

      <div className={styles.section}>
        <div className={styles.sectionTitle}>STATUS</div>
        <div className={styles.grid}>
          <Row label="UPD" value={timeAgo(entity.timestamp)} />
        </div>
      </div>
    </>
  );
}

// ─── Component ───────────────────────────────────────────────────

export default function AircraftPopup() {
  const selectedEntityId = useGlobeStore((s) => s.selectedEntityId);
  const trackedEntityId = useGlobeStore((s) => s.trackedEntityId);
  const setSelectedEntity = useGlobeStore((s) => s.setSelectedEntity);
  const entity = useEntityStore((s) =>
    selectedEntityId ? s.entities.get(selectedEntityId) : undefined,
  );
  const isMobile = useIsMobile();

  const { style: dragStyle, onMouseDown, isDragging, reset } = useDraggable(296, 32);
  const prevEntityRef = useRef<string | null>(null);

  useEffect(() => {
    if (selectedEntityId && selectedEntityId !== prevEntityRef.current) {
      reset();
      prevEntityRef.current = selectedEntityId;
    }
  }, [selectedEntityId, reset]);

  useEffect(() => {
    if (!selectedEntityId) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { setSelectedEntity(null); stopTracking(); }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedEntityId, setSelectedEntity]);

  if (!selectedEntityId || !entity || entity.type !== 'aircraft') return null;

  const props = entity.properties as Record<string, unknown>;
  const callsign = (props.callsign as string) || '—';
  const icao24 = (props.icao24 as string) || '—';

  const handleClose = () => { setSelectedEntity(null); stopTracking(); };

  const contentEntity = {
    position: entity.position,
    heading: entity.heading,
    source: entity.source,
    timestamp: entity.timestamp,
    properties: props,
  };

  // Desktop popup
  const desktopPopup = (
    <div
      className={styles.popup}
      style={dragStyle}
      id="aircraft-popup"
      data-draggable
      onMouseDown={(e) => e.stopPropagation()}
    >
      <div
        className={styles.header}
        onMouseDown={onMouseDown}
        style={{ cursor: isDragging ? 'grabbing' : 'grab' }}
      >
        <div className={styles.headerLeft}>
          <span className={styles.callsign}>{callsign}</span>
          <span className={styles.icao}>{icao24.toUpperCase()}</span>
        </div>
        <button className={styles.closeBtn} onClick={handleClose} title="Schliessen (ESC)" id="popup-close">✕</button>
      </div>
      <AircraftContent entity={contentEntity} trackedEntityId={trackedEntityId} />
    </div>
  );

  if (isMobile) {
    return (
      <EntityBottomSheet
        visible={true}
        entityName={callsign}
        entitySub={icao24.toUpperCase()}
        icon="✈️"
        onClose={handleClose}
        desktopPopup={desktopPopup}
      >
        <AircraftContent entity={contentEntity} trackedEntityId={trackedEntityId} />
      </EntityBottomSheet>
    );
  }

  return desktopPopup;
}

// ─── Row Helper ──────────────────────────────────────────────────

function Row({ label, value, suffix }: { label: string; value: string; suffix?: string }) {
  return (
    <div className={styles.row}>
      <span className={styles.label}>{label}</span>
      <span className={styles.value}>
        {value}{suffix && <span className={styles.suffix}>{suffix}</span>}
      </span>
    </div>
  );
}
